import { z } from 'zod'
import { ANALYTICS_TABS } from '@/types/backofficeAnalytics.types'

/**
 * Search params for the Analytics console (`analytics/index.tsx`). Extracted
 * to its own module (sc-1212 Forecasts view) so the Forecasts sub-route
 * (`analytics/forecasts/index.tsx`) can reuse it verbatim as the shape it
 * round-trips through its own breadcrumb `Link` back to the console — the
 * console's `Link` to Forecasts passes its whole current `search` object
 * through, and Forecasts' own `Link` back passes it straight through again,
 * so the console lands exactly where the reader left it. Two route files
 * importing a shared schema module rather than one importing the other,
 * which TanStack Router's file-based routing doesn't expect.
 *
 * Multi-selects travel as comma-joined strings (parsed by
 * `useAnalyticsFilters`/consumers) so the React Query key stays stable — an
 * array literal would be a fresh identity every render and refetch on each
 * one.
 */
export const analyticsSearchSchema = z.object({
  /** See {@link ANALYTICS_TABS} — the one place this vocabulary is written. */
  tab: z.enum(ANALYTICS_TABS).optional().catch(undefined),
  range: z
    .enum(['30d', '90d', '365d', 'month', 'custom'])
    .optional()
    .catch(undefined),
  /** ISO bounds for `range=custom`. */
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  /** `YYYY-MM` for `range=month`, in the reporting zone. */
  month: z.string().optional().catch(undefined),
  /** Which preset produced range/from/to/month — see
   * `lib/analytics-range-presets`. */
  preset: z
    .enum([
      '7d',
      '30d',
      '90d',
      'thisMonth',
      'lastMonth',
      'qtd',
      'ytd',
      '365d',
      'custom',
    ])
    .optional()
    .catch(undefined),
  experience: z.string().optional().catch(undefined),
  department: z.string().optional().catch(undefined),
  /** CSV of selected user ids — see `AnalyticsFilters.person`. */
  person: z.string().optional().catch(undefined),
  /** People table sort, `<column>_<asc|desc>` — see lib/column-sort. */
  peopleSort: z.string().optional().catch(undefined),
  /** Id of the applied saved view (sc-1212) — see `useAnalyticsViews`. */
  savedView: z.string().optional().catch(undefined),
})

export type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>
